import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useChangePhoneMutation } from '@/api/user/userApiSlice';
import { useParams } from 'react-router-dom';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import AppTextField, {
	InputType,
} from '@/common/components/form-inputs/AppTextField';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useGetCountriesQuery } from '@/api/common';
interface Props {
	formData: {
		phone: string;
		countryCodeId: string;
	};
}

const ChangePhoneModal = ({ formData }: Props) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [changePhone] = useChangePhoneMutation();
	const { id } = useParams();

	const { data: countryList }: any = useGetCountriesQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		phone: Yup.string().test(
			'phone',
			() => t('errorMessage.phone'),
			(phone, context) => {
				const countryCodeId = context.parent.countryCodeId;
				return (
					(phone?.length == 8 && !!countryCodeId) ||
					phone?.length == 0 ||
					(!!phone && !!countryCodeId) ||
					!phone
				);
			}
		),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		methods.setValue('phone', formData.phone);
		methods.setValue('countryCodeId', formData.countryCodeId);
	}, [formData]);

	const watchCountryCodeId = methods.watch('countryCodeId');
	const watchPhone = methods.watch('phone');
	useEffect(() => {
		if (watchCountryCodeId) methods.trigger('phone');
	}, [watchCountryCodeId, watchPhone]);

	const handleClose = () => {
		setOpen(false);
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		const response = await changePhone({ ...data, userId: id });
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.changePhoneSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	return (
		<>
			<IconButton
				color='primary'
				onClick={() => {
					setOpen(true);
				}}
			>
				<CreateOutlinedIcon />
			</IconButton>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>{t('user.phoneNumber')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0} marginBottom={2}>
								<Grid item xs={12}>
									<AppTextField
										inputType={InputType.number}
										maxLength={20}
										control={methods.control}
										name='phone'
										label={t('user.phoneNumber')}
										InputProps={{
											startAdornment: (
												<AppDropDownMenu
													control={methods.control}
													formControlStyle={{
														minWidth: 100,
														height: '100%',
													}}
													selectStyle={{
														backgroundColor: 'transparent',
													}}
													isFullWidth={false}
													isFormHelperText={false}
													name='countryCodeId'
													options={(countryList?.data || []).map(
														(item: any) => {
															return {
																name: item.countryCode,
																id: item.countryCodeId,
															};
														}
													)}
													required
												/>
											),
										}}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ChangePhoneModal;
